import React from 'react';
import { formatLocalDateTime, getInitials, getFileType } from '../utils/dataMapping';

const TimelineItem = ({ message, isFirst = false, isLast = false, onFilePreview, onFileDownload }) => {
  console.log('TimelineItem received message:', message);

  if (!message) {
    return null;
  }

  const {
    id,
    message: text, // API returns 'message' field
    created_at,
    user,
    agent,
    status,
    files,
    ticket_status
  } = message;

  // Determine sender information - API returns user object with name and type
  const senderName = user?.name || agent?.name || 'Unknown';
  const senderImage = user?.image || agent?.image;
  const senderInitials = getInitials(senderName);
  const isCurrentUser = message.is_current_user || false;

  // Validate and sanitize text
  let displayText = text;
  if (typeof text !== 'string') {
    displayText = typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text || 'No message');
  }

  // Get user type for styling based on API response
  const getUserTypeColor = () => {
    if (agent?.name) return 'bg-orange-500 text-white';
    if (user?.type === 'employee') return 'bg-gray-200 text-gray-700';
    if (user?.name) return 'bg-gray-200 text-gray-700';
    return 'bg-blue-500 text-white';
  };

  // Format files if present
  const renderFiles = () => {
    if (!files || !Array.isArray(files) || files.length === 0) return null;

    return (
      <div className="mt-3 flex flex-wrap gap-2">
        {files.map((file, index) => {
          if (!file) return null;

          // Handle both string (file path) and object formats
          let fileName, filePath;
          if (typeof file === 'string') {
            filePath = file;
            fileName = file.split('/').pop();
          } else {
            fileName = file.name || file.file_name || 'Unknown file';
            filePath = file.path || file.file_path || file;
          }
          const fileType = getFileType(fileName);

          const typeIcons = {
            image: '🖼️',
            pdf: '📄',
            video: '🎬',
            audio: '🎵',
            other: '📎'
          };

          return (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md border border-gray-200"
            >
              <span className="text-sm">{typeIcons[fileType] || '📎'}</span>
              <span className="text-sm text-gray-700 truncate max-w-[120px]">{fileName}</span>
              {onFilePreview && filePath && (
                <button
                  onClick={() => onFilePreview(filePath)}
                  className="text-blue-500 hover:text-blue-700 text-xs font-medium ml-1"
                  title="Preview"
                >
                  👁️
                </button>
              )}
              {onFileDownload && filePath && (
                <button
                  onClick={() => onFileDownload(filePath, fileName)}
                  className="text-gray-500 hover:text-gray-700 text-xs font-medium"
                  title="Download"
                >
                  ⬇️
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`relative flex items-start gap-4 w-full ${isFirst ? '' : 'pt-2'}`}>
      {/* Avatar */}
      <div className={`
        w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-bold text-sm
        border-2 border-white shadow-sm mt-1 z-20
        ${getUserTypeColor()}
      `}>
        {senderImage ? (
          <img
            src={senderImage}
            alt={senderName}
            className="w-full h-full rounded-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerText = senderInitials;
            }}
          />
        ) : (
          <span>{senderInitials}</span>
        )}
      </div>

      {/* Message Bubble */}
      <div className="flex-1 bg-gray-50 border border-gray-100 rounded-lg p-3 relative">
        {/* Arrow for bubble */}
        <div className="absolute top-4 -left-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-gray-100"></div>
        <div className="absolute top-4 -left-[7px] w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-gray-50 z-10"></div>

        {/* Header */}
        <div className="flex justify-between items-start mb-1">
          <h4 className="text-sm font-bold text-gray-800">{senderName}</h4>
          <span className="text-xs text-gray-400">{formatLocalDateTime(created_at)}</span>
        </div>

        {/* Status change indicator */}
        {ticket_status && (
          <div className="mb-2">
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
              Status changed to: {ticket_status}
            </span>
          </div>
        )}

        {/* Message text */}
        <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
          {displayText}
        </p>

        {/* File attachments */}
        {renderFiles()}
      </div>
    </div>
  );
};

export default TimelineItem;
